import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  Button,
  Drawer,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  IconButton,
  Divider,
  Pagination,
  Switch,
  FormHelperText,
  Collapse,
  InputAdornment
} from '@mui/material';
import {
  AccessTime,
  CalendarToday,
  PriorityHigh,
  NotificationsActive,
  Add,
  Close,
  FilterList,
  Edit,
  Delete,
  Refresh,
  SearchOff,
  CheckCircle,
  Error,
  Warning,
  Search as SearchIcon,
  Clear
} from '@mui/icons-material';
import Search from "../../../components/common/search/Search";
// Option A: If axiosInstance exists but different path
import axiosInstance from "../../../utils/axios/axiosInstance";


import { toast } from 'react-toastify';

// Option A: If loading.gif exists but different path
import loadingGif from '../../../assets/images/loading.gif';

// Option B: If loading.gif doesn't exist, replace the loading component with:
// const loadingGif = null;

const TodoList = () => {
  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // State declarations
  const [todos, setTodos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);

  // Filter and Pagination states
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // 6 todos per page

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 1, // Default fallback, will be updated when categories load
    due_date: '',
    time: '',
    priority_level: 1,
    notification_type: [1] // Default to email notifications
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  // Helper function to get current date in YYYY-MM-DD format (for HTML input)
  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Helper function to get current time in HH:MM format (24-hour for input)
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Helper function to get current date in MM/DD/YYYY format
  const getCurrentDateFormatted = () => {
    const today = new Date();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to validate date (handles both MM/DD/YYYY and YYYY-MM-DD formats)
  const validateDateInput = useCallback((dateString) => {
    if (!dateString) {
      return 'Due date is required';
    }

    // Handle both MM/DD/YYYY and YYYY-MM-DD formats
    let date;
    if (dateString.includes('/')) {
      // MM/DD/YYYY format
      const dateRegex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(\d{4})$/;
      if (!dateRegex.test(dateString)) {
        return 'Please enter date in MM/DD/YYYY format';
      }
      const [month, day, year] = dateString.split('/').map(num => parseInt(num, 10));
      date = new Date(year, month - 1, day);
      
      // Check if the constructed date matches the input (handles invalid dates like 02/30/2025)
      if (date.getFullYear() !== year || 
          date.getMonth() !== month - 1 || 
          date.getDate() !== day) {
        return 'Invalid date. Please check month, day, and year values.';
      }
    } else if (dateString.includes('-')) {
      // YYYY-MM-DD format from HTML5 date input
      const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
      if (!dateRegex.test(dateString)) {
        return 'Invalid date format';
      }
      date = new Date(dateString);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date selected';
      }
    } else {
      return 'Invalid date format. Expected MM/DD/YYYY or YYYY-MM-DD';
    }

    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) {
      return 'Due date cannot be in the past';
    }

    // Check if date is too far in the future (2 years)
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    if (date > maxDate) {
      return 'Due date cannot be more than 2 years in the future';
    }

    return null;
  }, []);

  // Convert MM/DD/YYYY to YYYY-MM-DD for API
  const convertDateForAPI = useCallback((mmddyyyy) => {
    if (!mmddyyyy) return '';
    if (!mmddyyyy.includes('/')) return mmddyyyy; // Already in YYYY-MM-DD format
    
    try {
      const [month, day, year] = mmddyyyy.split('/');
      const paddedMonth = month.padStart(2, '0');
      const paddedDay = day.padStart(2, '0');
      return `${year}-${paddedMonth}-${paddedDay}`;
    } catch (error) {
      console.error('Error converting date for API:', error);
      return mmddyyyy;
    }
  }, []);

  // Convert YYYY-MM-DD to MM/DD/YYYY for display
  const convertDateForDisplay = useCallback((yyyymmdd) => {
    if (!yyyymmdd) return '';
    if (!yyyymmdd.includes('-')) return yyyymmdd; // Already in MM/DD/YYYY format
    
    try {
      const [year, month, day] = yyyymmdd.split('-');
      // Remove leading zeros and return as MM/DD/YYYY
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    } catch (error) {
      console.error('Error converting date for display:', error);
      return yyyymmdd;
    }
  }, []);

  // Form validation functions
  const validateTitle = useCallback((title) => {
    const trimmedTitle = title?.trim() || '';
    
    if (!trimmedTitle) {
      return 'Task title is required';
    }
    if (trimmedTitle.length < 3) {
      return 'Title must be at least 3 characters long';
    }
    if (trimmedTitle.length > 200) {
      return 'Title must be less than 200 characters';
    }
    if (!/^[a-zA-Z0-9\s\-_.,!?()]+$/.test(trimmedTitle)) {
      return 'Title contains invalid characters';
    }
    return null;
  }, []);

  const validateDescription = useCallback((description) => {
    const trimmedDescription = description?.trim() || '';
    
    if (trimmedDescription.length > 1000) {
      return 'Description must be less than 1000 characters';
    }
    return null;
  }, []);

  const validateDueDate = useCallback((dateString) => {
    return validateDateInput(dateString);
  }, [validateDateInput]);

  const validateTime = useCallback((timeString) => {
    if (!timeString) {
      return 'Time is required';
    }
    
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(timeString)) {
      return 'Invalid time format (HH:MM)';
    }
    
    return null;
  }, []);

  const validateCategory = useCallback((categoryId) => {
    if (!categoryId) {
      return 'Category is required';
    }
    
    const numericId = parseInt(categoryId);
    if (isNaN(numericId) || numericId < 1) {
      return 'Please select a valid category';
    }
    
    if (categories.length > 0 && !categories.find(cat => cat.id === numericId)) {
      return 'Selected category does not exist';
    }
    
    return null;
  }, [categories]);

  const validatePriority = useCallback((priorityLevel) => {
    const numericPriority = parseInt(priorityLevel);
    if (isNaN(numericPriority) || ![1, 2, 3].includes(numericPriority)) {
      return 'Please select a valid priority level';
    }
    return null;
  }, []);

  const validateNotificationTypes = useCallback((notificationTypes) => {
    if (!Array.isArray(notificationTypes)) {
      return 'Invalid notification format';
    }
    
    const validTypes = [1, 2, 3];
    const invalidTypes = notificationTypes.filter(type => !validTypes.includes(parseInt(type)));
    
    if (invalidTypes.length > 0) {
      return 'Invalid notification type selected';
    }
    
    return null;
  }, []);

  // Combined form validation
  const validateForm = useCallback((data = formData) => {
    const errors = {};
    
    const titleError = validateTitle(data.title);
    if (titleError) errors.title = titleError;
    
    const descriptionError = validateDescription(data.description);
    if (descriptionError) errors.description = descriptionError;
    
    const dueDateError = validateDueDate(data.due_date);
    if (dueDateError) errors.due_date = dueDateError;
    
    const timeError = validateTime(data.time);
    if (timeError) errors.time = timeError;
    
    const categoryError = validateCategory(data.category);
    if (categoryError) errors.category = categoryError;
    
    const priorityError = validatePriority(data.priority_level);
    if (priorityError) errors.priority_level = priorityError;
    
    // Only validate notifications if user has interacted with them
    if (validationTouched.notification_type) {
      const notificationError = validateNotificationTypes(data.notification_type);
      if (notificationError) errors.notification_type = notificationError;
    }
    
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);
    
    return { isValid, errors };
  }, [formData, validateTitle, validateDescription, validateDueDate, validateTime, validateCategory, validatePriority, validateNotificationTypes, validationTouched.notification_type]);

  // Real-time field validation
  const validateField = useCallback((fieldName, value) => {
    let error = null;
    
    switch (fieldName) {
      case 'title':
        error = validateTitle(value);
        break;
      case 'description':
        error = validateDescription(value);
        break;
      case 'due_date':
        error = validateDueDate(value);
        break;
      case 'time':
        error = validateTime(value);
        break;
      case 'category':
        error = validateCategory(value);
        break;
      case 'priority_level':
        error = validatePriority(value);
        break;
      case 'notification_type':
        error = validateNotificationTypes(value);
        break;
      default:
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error;
  }, [validateTitle, validateDescription, validateDueDate, validateTime, validateCategory, validatePriority, validateNotificationTypes]);

  // Mark field as touched
  const markFieldAsTouched = useCallback((fieldName) => {
    setValidationTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const convertTo12HourFormat = useCallback((time24) => {
    if (!time24) return '';

    try {
      const [hours, minutes] = time24.split(':');
      const hour24 = parseInt(hours);
      const minute = minutes;

      let result;
      if (hour24 === 0) {
        result = `12:${minute} AM`;
      } else if (hour24 < 12) {
        result = `${hour24}:${minute} AM`;
      } else if (hour24 === 12) {
        result = `12:${minute} PM`;
      } else {
        result = `${hour24 - 12}:${minute} PM`;
      }

      console.log(`🕐 Time conversion: ${time24} -> ${result}`);
      return result;
    } catch (error) {
      console.error('Error converting time format:', error);
      return time24;
    }
  }, []);

  // Helper function to detect Safari browser
  const isSafari = useCallback(() => {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }, []);

  // Helper function to check if browser supports HTML5 time input
  const supportsTimeInput = useCallback(() => {
    if (isSafari()) return false;
    
    const input = document.createElement('input');
    input.setAttribute('type', 'time');
    return input.type === 'time';
  }, [isSafari]);
  const convertTo24HourFormat = useCallback((time12) => {
    if (!time12) return '';

    try {
      const timeRegex = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
      const match = time12.match(timeRegex);

      if (!match) return time12;

      let [, hours, minutes, period] = match;
      hours = parseInt(hours);

      if (period.toUpperCase() === 'AM') {
        if (hours === 12) hours = 0;
      } else {
        if (hours !== 12) hours += 12;
      }

      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    } catch (error) {
      console.error('Error converting time format:', error);
      return time12;
    }
  }, []);

  // Reset form data and validation
  const resetForm = useCallback(() => {
    setFormData({
      title: '',
      description: '',
      category: categories.length > 0 ? categories[0].id : 1,
      due_date: getCurrentDateFormatted(),
      time: getCurrentTime(),
      priority_level: 1,
      notification_type: [1] // Default to email notifications
    });
    setValidationErrors({});
    setValidationTouched({});
    setIsFormValid(false);
    setEditMode(false);
    setEditingTodo(null);
  }, [categories]);

  // Handle form input changes with validation
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark field as touched and validate
    markFieldAsTouched(field);
    validateField(field, value);
  }, [markFieldAsTouched, validateField]);

  // Handle notification type changes
  const handleNotificationChange = useCallback((value) => {
    const newNotificationTypes = formData.notification_type.includes(value)
      ? formData.notification_type.filter(type => type !== value)
      : [...formData.notification_type, value];
    
    setFormData(prev => ({
      ...prev,
      notification_type: newNotificationTypes
    }));
    
    markFieldAsTouched('notification_type');
    validateField('notification_type', newNotificationTypes);
  }, [formData.notification_type, markFieldAsTouched, validateField]);

  // Validate form whenever formData changes
  useEffect(() => {
    if (Object.keys(validationTouched).length > 0) {
      validateForm();
    }
  }, [formData, validateForm, validationTouched]);

  // Toggle todo completion status using new API
  const toggleTodoCompletion = useCallback(async (todoId, currentStatus) => {
    if (!todoId || !isMountedRef.current) return;

    try {
      // Ensure todoId is a valid number
      const numericTodoId = parseInt(todoId);
      if (isNaN(numericTodoId)) {
        throw new Error('Invalid todo ID');
      }

      const newStatus = currentStatus === 1 ? 0 : 1;

      console.log('🔄 Toggling todo completion:', {
        todoId: numericTodoId,
        currentStatus,
        expectedNewStatus: newStatus
      });

      // Prepare the request data - only ID required for toggle endpoint
      const requestData = { id: numericTodoId };
      console.log('📤 Sending toggle completion request:', requestData);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos/toggle-completion`,
        requestData
      );

      if (!isMountedRef.current) return;

      console.log('📥 Toggle Completion Response:', response.data);

      if (response.data.status === 200) {
        // Update the todo status in local state
        setTodos(prev => prev.map(todo =>
          todo.id === numericTodoId
            ? { ...todo, is_done: newStatus }
            : todo
        ));

        // Show success message from API response
        const message = response.data.message ||
          (newStatus === 1 ? '✅ Todo marked as completed!' : '📝 Todo marked as pending!');

        toast.success(message, {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to toggle todo completion');
      }
    } catch (err) {
      console.error('❌ Error toggling todo completion:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        todoId: todoId,
        currentStatus: currentStatus
      });

      let errorMessage = 'Failed to toggle todo completion';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;

        // Handle validation errors specifically
        if (err.response?.data?.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = `Validation Error: ${errors.join(', ')}`;
          console.error('❌ Validation errors:', errors);
        }
      } else if (err.response?.data?.errors) {
        if (Array.isArray(err.response.data.errors)) {
          errorMessage = `Validation Error: ${err.response.data.errors.join(', ')}`;
        } else {
          errorMessage = `Validation Error: ${err.response.data.errors}`;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    }
  }, []);

  // Mark todo as done (wrapper function for better UX)
  const markTodoAsDone = useCallback((todoId) => {
    const todo = todos.find(t => t.id === parseInt(todoId));
    if (todo) {
      toggleTodoCompletion(todoId, todo.is_done);
    }
  }, [todos, toggleTodoCompletion]);

  // Mark todo as undone (wrapper function for better UX)
  const markTodoAsUndone = useCallback((todoId) => {
    const todo = todos.find(t => t.id === parseInt(todoId));
    if (todo) {
      toggleTodoCompletion(todoId, todo.is_done);
    }
  }, [todos, toggleTodoCompletion]);

  // Handle edit todo
  const handleEdit = useCallback((todo) => {
    setEditMode(true);
    setEditingTodo(todo);

    // Parse notification type if it's a string
    let notificationTypes = [];
    try {
      if (typeof todo.notification_type === 'string') {
        notificationTypes = JSON.parse(todo.notification_type);
      } else if (Array.isArray(todo.notification_type)) {
        notificationTypes = todo.notification_type;
      }
    } catch (e) {
      console.error('Error parsing notification types for edit:', e);
      notificationTypes = [];
    }

    // Convert due_date to MM/DD/YYYY format for display
    let displayDate = todo.due_date;
    if (todo.due_date) {
      // If it's already in MM/DD/YYYY format, use as is
      if (todo.due_date.includes('/')) {
        displayDate = todo.due_date;
      } else {
        // If it's in YYYY-MM-DD format, convert to MM/DD/YYYY
        displayDate = convertDateForDisplay(todo.due_date);
      }
    }

    // Handle nested category structure
    const categoryId = typeof todo.category === 'object' ? todo.category.id : todo.category;

    // Pre-populate form with todo data
    const editFormData = {
      title: todo.title || '',
      description: todo.description || '',
      category: categoryId || 1,
      due_date: displayDate || getCurrentDateFormatted(),
      time: todo.inputTime || convertTo24HourFormat(todo.time) || getCurrentTime(),
      priority_level: todo.priority_level || 1,
      notification_type: notificationTypes
    };

    setFormData(editFormData);
    
    // Reset validation state for edit mode
    setValidationErrors({});
    setValidationTouched({});
    
    // Validate the edit form data
    setTimeout(() => {
      validateForm(editFormData);
    }, 100);

    setOpen(true);
  }, [convertTo24HourFormat, convertDateForDisplay, validateForm]);

  // Update existing todo
  const updateTodo = useCallback(async () => {
    if (!isMountedRef.current || !editingTodo) return;

    // Mark required fields as touched for validation display
    const requiredFields = ['title', 'category', 'due_date', 'time', 'priority_level'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    // Validate form before submission
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare data - send date in YYYY-MM-DD format and time to 12-hour format for API
      const todoData = {
        id: editingTodo.id,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: parseInt(formData.category),
        due_date: convertDateForAPI(formData.due_date), // Convert MM/DD/YYYY to YYYY-MM-DD for API
        time: convertTo12HourFormat(formData.time), // Convert to AM/PM format
        priority_level: parseInt(formData.priority_level),
        notification_type: formData.notification_type // Send as array, not stringified
      };

      console.log('📤 Updating todo data:', todoData);
      console.log('📤 Date (YYYY-MM-DD):', todoData.due_date);
      console.log('📤 Original time (24h):', formData.time);
      console.log('📤 Converted time (12h):', todoData.time);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos/edit`,
        todoData
      );

      if (!isMountedRef.current) return;

      console.log('📥 API Response:', response.data);

      if (response.data.status === 200) {
        const updatedTodo = response.data.data;

        // Ensure notification_type is properly handled for display
        const processedTodo = {
          ...updatedTodo,
          // notification_type comes back as string from API, but we need it parsed for display
          notification_type: typeof updatedTodo.notification_type === 'string'
            ? updatedTodo.notification_type
            : JSON.stringify(updatedTodo.notification_type),
          // Add display time for consistency
          displayTime: updatedTodo.time,
          inputTime: convertTo24HourFormat(updatedTodo.time)
        };

        // Update the todo in the list
        setTodos(prev => prev.map(todo =>
          todo.id === editingTodo.id ? processedTodo : todo
        ));

        setOpen(false);
        resetForm();
        setError(null);

        toast.success('Todo updated successfully! ✅', {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to update todo');
      }
    } catch (err) {
      console.error('❌ Error updating todo:', err);

      let errorMessage = 'Failed to update todo';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;

        // Handle validation errors specifically
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');

          // Special handling for time format errors
          if (errors.some(error => error.includes('time field format'))) {
            errorMessage = 'Time format is invalid. Please ensure you select a valid time.';
            console.error('❌ Time conversion failed:', {
              originalTime: formData.time,
              convertedTime: convertTo12HourFormat(formData.time)
            });
          }
        }
      } else if (err.response?.data?.errors) {
        errorMessage = err.response.data.errors.join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, editingTodo, resetForm, convertTo12HourFormat, convertTo24HourFormat, markFieldAsTouched, validateForm]);

  // Create new todo
  const createTodo = useCallback(async () => {
    if (!isMountedRef.current) return;

    // Mark required fields as touched for validation display
    const requiredFields = ['title', 'category', 'due_date', 'time', 'priority_level'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    // Validate form before submission
    const { isValid, errors } = validateForm();
    
    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Prepare data - send date in YYYY-MM-DD format and time to 12-hour format for API
      const todoData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: parseInt(formData.category),
        due_date: convertDateForAPI(formData.due_date), // Convert MM/DD/YYYY to YYYY-MM-DD for API
        time: convertTo12HourFormat(formData.time), // Convert to AM/PM format
        priority_level: parseInt(formData.priority_level),
        notification_type: formData.notification_type // Send as array, not stringified
      };

      console.log('📤 Sending todo data:', todoData);
      console.log('📤 Date (YYYY-MM-DD):', todoData.due_date);
      console.log('📤 Original time (24h):', formData.time);
      console.log('📤 Converted time (12h):', todoData.time);

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos`,
        todoData
      );

      if (!isMountedRef.current) return;

      console.log('📥 API Response:', response.data);

      if (response.data.status === 200) {
        const newTodo = response.data.data;

        // Ensure notification_type is properly handled for display
        const processedTodo = {
          ...newTodo,
          // notification_type comes back as string from API, but we need it parsed for display
          notification_type: typeof newTodo.notification_type === 'string'
            ? newTodo.notification_type
            : JSON.stringify(newTodo.notification_type),
          // Add display time for consistency
          displayTime: newTodo.time,
          inputTime: convertTo24HourFormat(newTodo.time)
        };

        setTodos(prev => [...prev, processedTodo]);
        setOpen(false);
        resetForm();
        setError(null);

        toast.success('Todo created successfully! 🎉', {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data.message || 'Failed to create todo');
      }
    } catch (err) {
      console.error('❌ Error creating todo:', err);

      let errorMessage = 'Failed to create todo';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;

        // Handle validation errors specifically
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');

          // Special handling for time format errors
          if (errors.some(error => error.includes('time field format'))) {
            errorMessage = 'Time format is invalid. Please ensure you select a valid time.';
            console.error('❌ Time conversion failed:', {
              originalTime: formData.time,
              convertedTime: convertTo12HourFormat(formData.time)
            });
          }
        }
      } else if (err.response?.data?.errors) {
        errorMessage = err.response.data.errors.join(', ');
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, resetForm, convertTo12HourFormat, convertTo24HourFormat, markFieldAsTouched, validateForm]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    console.log('📝 Form submission:', formData);
    console.log('📝 Edit mode:', editMode);
    console.log('📝 Form validation state:', { isFormValid, validationErrors });

    if (editMode) {
      updateTodo();
    } else {
      createTodo();
    }
  }, [formData, editMode, createTodo, updateTodo, isFormValid, validationErrors]);

  // Delete todo
  const deleteTodo = useCallback(async (todoId) => {
    if (!todoId || !isMountedRef.current) return;

    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos/${todoId}`
      );

      if (!isMountedRef.current) return;

      if (response.data?.status === 200 || response.status === 200) {
        setTodos(prev => prev.filter(todo => todo.id !== todoId));

        toast.success('Todo deleted successfully!', {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to delete todo');
      }
    } catch (err) {
      console.error('❌ Error deleting todo:', err);

      let errorMessage = 'Failed to delete todo';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, []);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos/categories`
      );

      if (response.data.status === 200) {
        const categoriesData = response.data.data || [];
        console.log('📥 Fetched categories:', categoriesData);
        setCategories(categoriesData);
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories');
      }
    } catch (err) {
      console.error('❌ Error fetching categories:', err);
      // Use fallback categories if API fails
      setCategories([
        { id: 1, name: 'Personal' },
        { id: 2, name: 'Work' },
        { id: 3, name: 'Education' }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch todos and categories from API
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch categories first
        await fetchCategories();

        if (!mounted) return;

        // Then fetch todos
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/todos`
        );

        if (!mounted) return;

        if (response.data.status === 200) {
          const todosData = response.data.data || [];

          console.log('📥 Fetched todos:', todosData);

          // Process todos to ensure consistent time format for display
          const processedTodos = todosData.map(todo => ({
            ...todo,
            // Keep original API time format for display, store 24h format if needed for editing
            displayTime: todo.time, // Original AM/PM format from API
            inputTime: convertTo24HourFormat(todo.time) // Convert to 24h for HTML input if needed
          }));

          setTodos(processedTodos);

          if (todosData.length === 0) {
            toast.info('No Todo Notes found. Create your first task! 📝', {
              position: "top-center",
              autoClose: 3000,
            });
          }
        } else {
          throw new Error(response.data.message || 'Failed to fetch todos');
        }
      } catch (err) {
        console.error('❌ Error fetching data:', err);

        let errorMessage = 'Failed to fetch data';

        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }

        if (mounted) {
          setError(errorMessage);
          console.error('Error fetching data:', err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [convertTo24HourFormat, fetchCategories]);

  // Update form data when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && formData.category === 1 && !editMode) {
      setFormData(prev => ({
        ...prev,
        category: categories[0].id,
        due_date: prev.due_date || getCurrentDateFormatted(),
        time: prev.time || getCurrentTime(),
        notification_type: prev.notification_type.length === 0 ? [1] : prev.notification_type // Keep existing notifications or default to email
      }));
    }
  }, [categories, formData.category, editMode]);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to get priority label and color
  const getPriorityInfo = useCallback((level) => {
    switch (String(level)) {
      case '1': return { label: 'High', color: '#f44336', bgColor: '#ffebee', icon: '🔥' };
      case '2': return { label: 'Medium', color: '#ff9800', bgColor: '#fff3e0', icon: '⚡' };
      case '3': return { label: 'Low', color: '#4caf50', bgColor: '#e8f5e9', icon: '📝' };
      default: return { label: 'Unknown', color: '#9e9e9e', bgColor: '#f5f5f5', icon: '❓' };
    }
  }, []);

  // Helper function to get category info
  const getCategoryInfo = useCallback((categoryData) => {
    let category = null;
    
    // Handle both nested category object and category ID
    if (typeof categoryData === 'object' && categoryData !== null && categoryData.name) {
      // New API structure: category is an object with id and name
      category = categoryData;
    } else {
      // Fallback: category is just an ID, find in categories array
      const categoryId = typeof categoryData === 'object' ? categoryData.id : categoryData;
      category = categories.find(cat => cat.id === parseInt(categoryId));
    }
    
    if (category) {
      // Map category names to appropriate colors (no icons)
      const categoryName = category.name.toLowerCase();
      let color = '#795548';
      let bgColor = '#efebe9';

      switch (categoryName) {
        case 'personal':
          color = '#9c27b0';
          bgColor = '#f3e5f5';
          break;
        case 'works':
        case 'work':
          color = '#607d8b';
          bgColor = '#eceff1';
          break;
        case 'education':
        case 'academic':
          color = '#3f51b5';
          bgColor = '#e8eaf6';
          break;
        case 'shopping':
          color = '#ff9800';
          bgColor = '#fff3e0';
          break;
        case 'fitness':
          color = '#4caf50';
          bgColor = '#e8f5e9';
          break;
        case 'health':
          color = '#f44336';
          bgColor = '#ffebee';
          break;
        case 'finance':
          color = '#4caf50';
          bgColor = '#e8f5e9';
          break;
        case 'travel':
          color = '#2196f3';
          bgColor = '#e3f2fd';
          break;
        case 'projects':
          color = '#673ab7';
          bgColor = '#ede7f6';
          break;
        case 'chores':
          color = '#795548';
          bgColor = '#efebe9';
          break;
        default:
          break;
      }

      return {
        label: category.name,
        color,
        bgColor
      };
    }
    return { label: 'General', color: '#795548', bgColor: '#efebe9' };
  }, [categories]);

  // Helper function to format date to mm/dd/yyyy
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'No date';
    try {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }, []);

  // Helper function to get notification type labels
  const getNotificationLabels = useCallback((notificationTypes) => {
    if (!Array.isArray(notificationTypes) || notificationTypes.length === 0) {
      return [];
    }

    return notificationTypes.map(type => {
      switch (parseInt(type)) {
        case 1: return 'Email';
        case 2: return 'SMS';
        case 3: return 'Push';
        default: return `Type ${type}`;
      }
    });
  }, []);

  // Handle opening add new todo modal
  const handleAddNew = useCallback(() => {
    setEditMode(false);
    setEditingTodo(null);
    resetForm();
    setOpen(true);
  }, [resetForm]);

  // Close modal handler
  const closeModal = useCallback(() => {
    setOpen(false);
    resetForm();
    setError(null);
  }, [resetForm]);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  }, []);

  // Filter and sort todos - UPDATED with search functionality
  const getFilteredAndSortedTodos = useCallback(() => {
    let filteredTodos = [...todos];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredTodos = filteredTodos.filter(todo => {
        const titleMatch = todo.title?.toLowerCase().includes(query);
        const descriptionMatch = todo.description?.toLowerCase().includes(query);
        // Handle nested category structure
        const categoryName = typeof todo.category === 'object' ? todo.category.name : 
                           categories.find(cat => cat.id === todo.category)?.name;
        const categoryMatch = categoryName?.toLowerCase().includes(query);
        return titleMatch || descriptionMatch || categoryMatch;
      });
    }

    // Apply priority filter
    if (priorityFilter !== 'all') {
      const priorityLevel = parseInt(priorityFilter);
      filteredTodos = filteredTodos.filter(todo => todo.priority_level === priorityLevel);
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      const categoryId = parseInt(categoryFilter);
      filteredTodos = filteredTodos.filter(todo => {
        // Handle nested category structure
        const todoCategoryId = typeof todo.category === 'object' ? todo.category.id : todo.category;
        return todoCategoryId === categoryId;
      });
    }

    // Separate completed and pending todos
    const pendingTodos = filteredTodos.filter(todo => todo.is_done !== 1);
    const completedTodos = filteredTodos.filter(todo => todo.is_done === 1);

    // Sort pending todos by earliest date first (most urgent/upcoming tasks first)
    pendingTodos.sort((a, b) => {
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return dateA - dateB; // Earliest first (most urgent)
    });

    // Sort completed todos by earliest date first as well
    completedTodos.sort((a, b) => {
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return dateA - dateB; // Earliest first
    });

    // Return pending todos first, then completed todos
    return [...pendingTodos, ...completedTodos];
  }, [todos, priorityFilter, categoryFilter, searchQuery, categories]);

  // Get paginated todos
  const getPaginatedTodos = useCallback(() => {
    const filteredTodos = getFilteredAndSortedTodos();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTodos.slice(startIndex, endIndex);
  }, [getFilteredAndSortedTodos, currentPage, itemsPerPage]);

  // Calculate total pages
  const totalPages = Math.ceil(getFilteredAndSortedTodos().length / itemsPerPage);

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setCurrentPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handle priority filter change
  const handlePriorityFilterChange = useCallback((event, newFilter) => {
    if (newFilter !== null) {
      setPriorityFilter(newFilter);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  }, []);

  // Handle category filter change
  const handleCategoryFilterChange = useCallback((event, newFilter) => {
    if (newFilter !== null) {
      setCategoryFilter(newFilter);
      setCurrentPage(1); // Reset to first page when filter changes
    }
  }, []);

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [priorityFilter, categoryFilter, searchQuery]);

  // Helper function to get field error state
  const getFieldError = useCallback((fieldName) => {
    return validationTouched[fieldName] && validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  // Helper function to check if field is valid
  const isFieldValid = useCallback((fieldName) => {
    return validationTouched[fieldName] && !validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  // Early returns for loading/error states
  if (loading || categoriesLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, fontFamily: '"Saira", sans-serif' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          {loadingGif ? (
            <img
              src={loadingGif}
              alt="Loading..."
              style={{ width: '80px' }}
            />
          ) : (
            <CircularProgress size={60} />
          )}
          <Typography variant="body1" sx={{ mt: 2, fontFamily: '"Saira", sans-serif' }}>
            {loading && categoriesLoading ? 'Loading your data...' :
              loading ? 'Loading your todos...' :
                'Loading categories...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error && !todos.length) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, fontFamily: '"Saira", sans-serif' }}>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
              <strong>Error!</strong> {error}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{
              mt: 2,
              fontFamily: '"Saira", sans-serif',
              background: '#ce55b9',
              '&:hover': {
                background: '#b8479a',
              }
            }}
          >
            Retry
          </Button>
        </Box>
      </Container>
    );
  }

  const filteredAndSortedTodos = getFilteredAndSortedTodos();
  const paginatedTodos = getPaginatedTodos();

  return (
    <Grid container spacing={2}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body ">
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2">To-Do Lists</h3>
                  </div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    <Button variant="contained" color="primary" className="!w-fit !px-3 !h-10" onClick={handleAddNew}>
                      + Add New Todo
                    </Button>
                  </div>
                </div>

                <div className="row filter-panel mt-4">
                  <div className="col-md-4">
                    <div className="serarch-bar">
                      <Search
                        value={searchQuery}
                        handleInputChange={(e) => handleSearchChange(e)}
                      />
                    </div>
                  </div>
                  <div className="col-md-4"></div>
                  <div className="col-md-4">
                    <div className="w-full flex items-center justify-end gap-2">
                     <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ce55b9',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ce55b9'
                    }
                  }}
                >
                  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
                    Priority
                  </InputLabel>
                  <Select
                    value={priorityFilter}
                    label="Priority"
                    onChange={(e) => setPriorityFilter(e.target.value)}
                    sx={{
                      fontFamily: '"Saira", sans-serif',
                      fontWeight: 500,
                      '& .MuiSelect-select': {
                        fontFamily: '"Saira", sans-serif'
                      }
                    }}
                  >
                    <MenuItem value="all" sx={{ fontFamily: '"Saira", sans-serif' }}>
                      All Priorities
                    </MenuItem>
                    <MenuItem value="1" sx={{ fontFamily: '"Saira", sans-serif' }}>
                      🔥 High
                    </MenuItem>
                    <MenuItem value="2" sx={{ fontFamily: '"Saira", sans-serif' }}>
                      ⚡ Medium
                    </MenuItem>
                    <MenuItem value="3" sx={{ fontFamily: '"Saira", sans-serif' }}>
                      📝 Low
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Category Filter */}
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ce55b9',
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#ce55b9'
                    }
                  }}
                >
                  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
                    Category
                  </InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    sx={{
                      fontFamily: '"Saira", sans-serif',
                      fontWeight: 500,
                      '& .MuiSelect-select': {
                        fontFamily: '"Saira", sans-serif'
                      }
                    }}
                  >
                    <MenuItem value="all" sx={{ fontFamily: '"Saira", sans-serif' }}>
                      All Categories
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem
                        key={category.id}
                        value={category.id.toString()}
                        sx={{ fontFamily: '"Saira", sans-serif' }}
                      >
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                    </div>
                  </div>
                </div>

                {/* todo list */}

                      {error && todos.length > 0 && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            fontFamily: '"Saira", sans-serif'
          }}
          onClose={() => setError(null)}
        >
          <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
            {error}
          </Typography>
        </Alert>
      )}
      

                      {/* list */}
                       <Grid container spacing={3}>
              {paginatedTodos.length > 0 ? (
                paginatedTodos.map((todo) => {
                  const priorityInfo = getPriorityInfo(todo.priority_level);
                  const categoryInfo = getCategoryInfo(todo.category); // Pass the nested category object
                  const isCompleted = todo.is_done === 1;
                  let notificationTypes = [];

                  try {
                    // Handle both string and array formats from API
                    if (typeof todo.notification_type === 'string') {
                      notificationTypes = JSON.parse(todo.notification_type);
                    } else if (Array.isArray(todo.notification_type)) {
                      notificationTypes = todo.notification_type;
                    }
                  } catch (e) {
                    console.error('Error parsing notification types:', e);
                    console.log('Original notification_type:', todo.notification_type);
                    notificationTypes = [];
                  }

                  return (
                    <Grid item xs={12} sm={6} lg={4} key={todo.id}>

                      <Card
                        sx={{
                          height: '100%',
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          transform: isCompleted ? 'scale(0.98)' : 'scale(1)',
                          opacity: isCompleted ? 0.8 : 1,
                          background: isCompleted
                            ? '#e8f5e9'
                            : 'white',
                          border: isCompleted ? '2px solid #4caf50' : '1px solid #e0e0e0',
                          borderRadius: 3,
                          overflow: 'hidden',
                          fontFamily: '"Saira", sans-serif',
                          '&:hover': {
                            transform: isCompleted ? 'scale(0.99)' : 'scale(1.02)',
                            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                          }
                        }}
                      >
                        {/* Priority indicator stripe */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: priorityInfo.color,
                          }}
                        />

                        <CardContent sx={{ p: 3 }}>
                          {/* Header with completion status */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                  color: isCompleted ? 'text.secondary' : 'text.primary',
                                  textDecoration: isCompleted ? 'line-through' : 'none',
                                  fontWeight: 600,
                                  lineHeight: 1.3,
                                  fontFamily: '"Saira", sans-serif',
                                  mb: 1
                                }}
                              >
                                {todo.title}
                              </Typography>

                              {/* Description */}
                              {todo.description && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isCompleted ? 'text.secondary' : 'text.secondary',
                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                    fontFamily: '"Saira", sans-serif',
                                    mb: 2,
                                    lineHeight: 1.4,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                  }}
                                >
                                  {todo.description}
                                </Typography>
                              )}

                              {/* Completion Status Switch */}
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mb: 2,
                                p: 2,
                                backgroundColor: isCompleted ? 'rgba(76, 175, 80, 0.08)' : 'rgba(33, 150, 243, 0.08)',
                                borderRadius: 2,
                                border: `1px solid ${isCompleted ? 'rgba(76, 175, 80, 0.2)' : 'rgba(33, 150, 243, 0.2)'}`
                              }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: '"Saira", sans-serif',
                                    fontWeight: 500,
                                    color: isCompleted ? 'text.secondary' : 'text.primary',
                                    minWidth: 60
                                  }}
                                >
                                  {isCompleted ? 'Done' : 'Pending'}
                                </Typography>

                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={isCompleted}
                                      onChange={() => toggleTodoCompletion(todo.id, todo.is_done)}
                                      disabled={submitting}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#4caf50',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#4caf50',
                                        },
                                        '& .MuiSwitch-track': {
                                          backgroundColor: '#e0e0e0',
                                        },
                                        '& .MuiSwitch-switchBase': {
                                          color: '#f5f5f5',
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{
                                      fontFamily: '"Saira", sans-serif',
                                      fontWeight: 500,
                                      color: isCompleted ? '#4caf50' : '#757575'
                                    }}>
                                      {submitting ? 'Updating...' : (isCompleted ? 'Completed' : 'Mark Complete')}
                                    </Typography>
                                  }
                                  sx={{ ml: 1 }}
                                />

                                {submitting && (
                                  <CircularProgress
                                    size={20}
                                    sx={{
                                      color: isCompleted ? '#4caf50' : '#2196f3',
                                      ml: 1
                                    }}
                                  />
                                )}
                              </Box>

                              {isCompleted && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#4caf50',
                                    fontWeight: 500,
                                    fontStyle: 'italic',
                                    fontFamily: '"Saira", sans-serif',
                                    mb: 1
                                  }}
                                >
                                  ✓ Task completed successfully!
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Category and Priority tags */}
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <Chip
                              label={categoryInfo.label}
                              size="small"
                              sx={{
                                backgroundColor: categoryInfo.bgColor,
                                color: categoryInfo.color,
                                fontWeight: 600,
                                fontFamily: '"Saira", sans-serif',
                                fontSize: '0.75rem'
                              }}
                            />
                            <Chip
                              label={`${priorityInfo.icon} ${priorityInfo.label}`}
                              size="small"
                              sx={{
                                backgroundColor: priorityInfo.bgColor,
                                color: priorityInfo.color,
                                fontWeight: 600,
                                fontFamily: '"Saira", sans-serif',
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>

                          {/* Date and Time info */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 1,
                              p: 1.5,
                              backgroundColor: 'rgba(0,0,0,0.03)',
                              borderRadius: 2
                            }}>
                              <AccessTime sx={{
                                fontSize: 18,
                                mr: 1.5,
                                color: '#f57c00'
                              }} />
                              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: '"Saira", sans-serif' }}>
                                {todo.displayTime || todo.time || 'No time set'}
                              </Typography>
                            </Box>
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 1.5,
                              backgroundColor: 'rgba(0,0,0,0.03)',
                              borderRadius: 2
                            }}>
                              <CalendarToday sx={{
                                fontSize: 18,
                                mr: 1.5,
                                color: '#1976d2'
                              }} />
                              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: '"Saira", sans-serif' }}>
                                {formatDate(todo.due_date) || 'No date set'}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Notifications */}
                          {notificationTypes && notificationTypes.length > 0 && (
                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              p: 1.5,
                              backgroundColor: 'rgba(156, 39, 176, 0.1)',
                              borderRadius: 2
                            }}>
                              <NotificationsActive sx={{
                                fontSize: 18,
                                mr: 1.5,
                                color: '#9c27b0'
                              }} />
                              <Typography variant="body2" sx={{
                                color: '#9c27b0',
                                fontWeight: 500,
                                fontFamily: '"Saira", sans-serif'
                              }}>
                                {getNotificationLabels(notificationTypes).join(', ')}
                              </Typography>
                            </Box>
                          )}

                          {/* Professional Actions Section */}
                          <Box sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            pt: 2,
                            borderTop: '1px solid #e0e0e0'
                          }}>
                            {/* Only show Edit button for pending todos */}
                            {!isCompleted && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<Edit sx={{ fontSize: 16 }} />}
                                onClick={() => handleEdit(todo)}
                                disabled={submitting || loading}
                                sx={{
                                  color: '#1976d2',
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  fontFamily: '"Saira", sans-serif',
                                  px: 2,
                                  py: 1,
                                  minHeight: 32,
                                  backgroundColor: 'transparent',
                                  border: '1px solid rgba(25, 118, 210, 0.2)',
                                  borderRadius: 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderColor: 'rgba(25, 118, 210, 0.3)',
                                    color: '#1565c0'
                                  },
                                  '&:disabled': {
                                    color: '#bdbdbd',
                                    borderColor: 'rgba(189, 189, 189, 0.2)'
                                  }
                                }}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Delete sx={{ fontSize: 16 }} />}
                              onClick={() => deleteTodo(todo.id)}
                              disabled={submitting || loading}
                              sx={{
                                color: '#d32f2f',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontFamily: '"Saira", sans-serif',
                                px: 2,
                                py: 1,
                                minHeight: 32,
                                backgroundColor: 'transparent',
                                border: '1px solid rgba(211, 47, 47, 0.2)',
                                borderRadius: 1,
                                '&:hover': {
                                  backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                  borderColor: 'rgba(211, 47, 47, 0.3)',
                                  color: '#c62828'
                                },
                                '&:disabled': {
                                  color: '#bdbdbd',
                                  borderColor: 'rgba(189, 189, 189, 0.2)'
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })
              ) : (
                // No todos found message
                <Grid item xs={12}>
                  <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    px: 4,
                    backgroundColor: '#fafafa',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                  }}>
                    <SearchOff sx={{
                      fontSize: 48,
                      color: '#bdbdbd',
                      mb: 2
                    }} />
                    <Typography variant="h6" sx={{
                      mb: 1,
                      fontWeight: 600,
                      fontFamily: '"Saira", sans-serif',
                      color: '#424242'
                    }}>
                      No Todo found
                    </Typography>
                    <Typography variant="body2" sx={{
                      mb: 4,
                      color: '#757575',
                      fontFamily: '"Saira", sans-serif',
                      maxWidth: 400,
                      mx: 'auto',
                      lineHeight: 1.5
                    }}>
                      {(priorityFilter !== 'all' || categoryFilter !== 'all' || searchQuery.trim()) ?
                        'No todos match your current search criteria. Try adjusting your search or filters.' :
                        'You haven\'t created any todos yet. Get started by creating your first task.'
                      }
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Add sx={{ fontSize: 16 }} />}
                        onClick={handleAddNew}
                        sx={{
                          backgroundColor: '#ce55b9',
                          color: 'white',
                          fontFamily: '"Saira", sans-serif',
                          fontWeight: 500,
                          textTransform: 'none',
                          px: 2,
                          minWidth: 120,
                          borderRadius: 1,
                          boxShadow: 'none',
                          '&:hover': {
                            backgroundColor: '#b8479a',
                            boxShadow: 'none'
                          }
                        }}
                      >
                        New Todo 
                      </Button>
                      {(priorityFilter !== 'all' || categoryFilter !== 'all' || searchQuery.trim()) && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Refresh sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            setPriorityFilter('all');
                            setCategoryFilter('all');
                            setSearchQuery('');
                            setCurrentPage(1);
                          }}
                          sx={{
                            borderColor: '#e0e0e0',
                            color: '#757575',
                            fontFamily: '"Saira", sans-serif',
                            fontWeight: 500,
                            textTransform: 'none',
                            px: 2,
                            minWidth: 120,
                            borderRadius: 1,
                            '&:hover': {
                              borderColor: '#bdbdbd',
                              backgroundColor: '#f5f5f5',
                              color: '#424242'
                            }
                          }}
                        >
                          Clear All
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>

                      {/* list */}

                {/* todo list */}

              </div>
            </div>
               {/* Pagination */}

               
                        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            mb: 2
          }}>
            <Typography variant="body2" sx={{
              color: '#6c757d',
              fontFamily: '"Saira", sans-serif',
              textAlign: 'center'
            }}>
              Showing {paginatedTodos.length} of {filteredAndSortedTodos.length} todos
              {searchQuery.trim() && (
                <span> • Search: "{searchQuery}"</span>
              )}
              {priorityFilter !== 'all' && (
                <span> • Priority: {getPriorityInfo(priorityFilter).label}</span>
              )}
              {categoryFilter !== 'all' && (
                <span> • Category: {getCategoryInfo(parseInt(categoryFilter)).label}</span>
              )}
              {filteredAndSortedTodos.length > 0 && <span> • Sorted by due date (upcoming first)</span>}
            </Typography>
          </Box>
          {totalPages > 1 && (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 4
            }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontFamily: '"Saira", sans-serif',
                    fontWeight: 600,
                    '&.Mui-selected': {
                      backgroundColor: '#ce55b9',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#b8479a',
                      }
                    }
                  }
                }}
              />
            </Box>
          )}
          
          </div>
        </div>
      </div>

      

      <Drawer
        anchor="right"
        open={open}
        onClose={closeModal}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400, md: 450 },
            maxWidth: '90vw',
            fontFamily: '"Saira", sans-serif'
          }
        }}
      >
        <form onSubmit={handleSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{
            background: '#ce55b9',
            color: 'white',
            p: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: '"Saira", sans-serif' }}>
                {editMode ? 'Edit Todo' : 'Add New Todo'}
              </Typography>
              
            </Box>
            <IconButton
              onClick={closeModal}
              disabled={submitting}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              <Close />
            </IconButton>
          </Box>

          
          
          {/* Content */}
          <Box sx={{
            flex: 1,
            p: 3,
            overflow: 'auto',
            background: '#ffffff'
          }}>
            <TextField
              fullWidth
              label="Task Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={() => markFieldAsTouched('title')}
              required
              error={!!getFieldError('title')}
              helperText={getFieldError('title') || 'Enter a descriptive title for your task (3-200 characters)'}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('title') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('title') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }
                }
              }}
              disabled={submitting}
              placeholder="What do you need to accomplish?"
              InputProps={{
                endAdornment: isFieldValid('title') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('title') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <TextField
              fullWidth
              label="Description (Optional)"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              onBlur={() => markFieldAsTouched('description')}
              multiline
              rows={3}
              error={!!getFieldError('description')}
              helperText={getFieldError('description') || ''}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f44336',
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isFieldValid('description') ? '#4caf50' : '#ce55b9',
                    }
                  }
                },
                '& .MuiInputLabel-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-focused': {
                    color: isFieldValid('description') ? '#4caf50' : '#ce55b9'
                  }
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: '"Saira", sans-serif',
                  '&.Mui-error': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }
                }
              }}
              disabled={submitting}
              placeholder="Provide more details about this task..."
              InputProps={{
                endAdornment: isFieldValid('description') ? (
                  <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : getFieldError('description') ? (
                  <Error sx={{ color: '#f44336', fontSize: 20 }} />
                ) : null
              }}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  sx={{ mb: 3 }}
                  error={!!getFieldError('category')}
                >
                  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    onBlur={() => markFieldAsTouched('category')}
                    disabled={submitting || categoriesLoading}
                    sx={{
                      borderRadius: 2,
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: isFieldValid('category') ? '#4caf50' : '#ce55b9',
                      }
                    }}
                    endAdornment={
                      isFieldValid('category') ? (
                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20, mr: 2 }} />
                      ) : getFieldError('category') ? (
                        <Error sx={{ color: '#f44336', fontSize: 20, mr: 2 }} />
                      ) : null
                    }
                  >
                    {categories.map((category) => {
                      return (
                        <MenuItem
                          key={category.id}
                          value={category.id}
                          sx={{ fontFamily: '"Saira", sans-serif' }}
                        >
                          {category.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {(getFieldError('category') || categoriesLoading) && (
                    <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
                      {getFieldError('category') || 'Loading categories...'}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl 
                  fullWidth 
                  sx={{ mb: 3 }}
                  error={!!getFieldError('priority_level')}
                >
                  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Priority</InputLabel>
                  <Select
                    value={formData.priority_level}
                    label="Priority"
                    onChange={(e) => handleInputChange('priority_level', e.target.value)}
                    onBlur={() => markFieldAsTouched('priority_level')}
                    disabled={submitting}
                    sx={{
                      borderRadius: 2,
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: isFieldValid('priority_level') ? '#4caf50' : '#ce55b9',
                      }
                    }}
                    endAdornment={
                      isFieldValid('priority_level') ? (
                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20, mr: 2 }} />
                      ) : getFieldError('priority_level') ? (
                        <Error sx={{ color: '#f44336', fontSize: 20, mr: 2 }} />
                      ) : null
                    }
                  >
                    <MenuItem value={1} sx={{ fontFamily: '"Saira", sans-serif' }}>🔥 High</MenuItem>
                    <MenuItem value={2} sx={{ fontFamily: '"Saira", sans-serif' }}>⚡ Medium</MenuItem>
                    <MenuItem value={3} sx={{ fontFamily: '"Saira", sans-serif' }}>📝 Low</MenuItem>
                  </Select>
                  {getFieldError('priority_level') && (
                    <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
                      {getFieldError('priority_level')}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={convertDateForAPI(formData.due_date)} // Convert MM/DD/YYYY to YYYY-MM-DD for input
                  onChange={(e) => {
                    const apiDate = e.target.value; // This is in YYYY-MM-DD format from the picker
                    const displayDate = convertDateForDisplay(apiDate); // Convert to MM/DD/YYYY for storage
                    handleInputChange('due_date', displayDate);
                  }}
                  onBlur={() => markFieldAsTouched('due_date')}
                  required
                  error={!!getFieldError('due_date')}
                  helperText={
                    getFieldError('due_date') || 'Click calendar icon to select date'
                  }
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f44336',
                        }
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: isFieldValid('due_date') ? '#4caf50' : '#ce55b9',
                        }
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontFamily: '"Saira", sans-serif',
                      '&.Mui-focused': {
                        color: isFieldValid('due_date') ? '#4caf50' : '#ce55b9'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      fontFamily: '"Saira", sans-serif',
                      margin: 0,
                      marginTop: '8px'
                    },
                    // Style the date picker button
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      filter: 'invert(0.5)',
                      cursor: 'pointer',
                      fontSize: '18px',
                      padding: '4px',
                      marginRight: '4px'
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                      filter: 'invert(0.3)',
                      backgroundColor: 'rgba(206, 85, 185, 0.1)',
                      borderRadius: '4px'
                    }
                  }}
                  disabled={submitting}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: getCurrentDate(), // Prevent selecting past dates
                    max: (() => {
                      const maxDate = new Date();
                      maxDate.setFullYear(maxDate.getFullYear() + 2);
                      return maxDate.toISOString().split('T')[0];
                    })(),
                    // Prevent manual keyboard input but allow picker
                    onKeyDown: (e) => {
                      // Allow only tab, escape, and arrow keys for navigation
                      if (!['Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                        e.preventDefault();
                      }
                    },
                    onInput: (e) => {
                      // Prevent pasting or direct input
                      e.preventDefault();
                    }
                  }}
                  InputProps={{
                    endAdornment: isFieldValid('due_date') ? (
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    ) : getFieldError('due_date') ? (
                      <Error sx={{ color: '#f44336', fontSize: 20 }} />
                    ) : null
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {!supportsTimeInput() ? (
                  // Safari/Fallback: Custom dropdown time picker
                  <FormControl 
                    fullWidth 
                    sx={{ mb: 3 }}
                    error={!!getFieldError('time')}
                  >
                    <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Time</InputLabel>
                    <Select
                      value={formData.time || ''}
                      label="Time"
                      onChange={(e) => handleInputChange('time', e.target.value)}
                      onBlur={() => markFieldAsTouched('time')}
                      disabled={submitting}
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        fontFamily: '"Saira", sans-serif',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: isFieldValid('time') ? '#4caf50' : '#ce55b9',
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography sx={{ color: '#9e9e9e', fontFamily: '"Saira", sans-serif' }}>
                              Select a time
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTime sx={{ fontSize: 18, color: '#f57c00' }} />
                            <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                              {convertTo12HourFormat(selected)}
                            </Typography>
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                            overflowY: 'auto'
                          }
                        }
                      }}
                      endAdornment={
                        isFieldValid('time') ? (
                          <CheckCircle sx={{ color: '#4caf50', fontSize: 20, mr: 2 }} />
                        ) : getFieldError('time') ? (
                          <Error sx={{ color: '#f44336', fontSize: 20, mr: 2 }} />
                        ) : null
                      }
                    >
                      <MenuItem disabled value="">
                        <Typography sx={{ fontStyle: 'italic', color: '#9e9e9e', fontFamily: '"Saira", sans-serif' }}>
                          Choose a time
                        </Typography>
                      </MenuItem>
                      
                      {/* Generate time options in 15-minute intervals */}
                      {Array.from({ length: 96 }, (_, index) => {
                        const totalMinutes = index * 15;
                        const hours = Math.floor(totalMinutes / 60);
                        const minutes = totalMinutes % 60;
                        const time24 = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                        const time12 = convertTo12HourFormat(time24);
                        
                        return (
                          <MenuItem 
                            key={time24} 
                            value={time24}
                            sx={{ fontFamily: '"Saira", sans-serif' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <AccessTime sx={{ fontSize: 16, color: '#f57c00' }} />
                              <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                                {time12}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                ml: 'auto', 
                                color: '#757575',
                                fontFamily: '"Saira", sans-serif'
                              }}>
                                {time24}
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                    
                    {getFieldError('time') ? (
                      <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
                        {getFieldError('time')}
                      </FormHelperText>
                    ) : (
                      <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
                        {formData.time ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{
                              color: '#666',
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              Selected: {convertTo12HourFormat(formData.time)}
                            </Typography>
                            <Chip
                              label={convertTo12HourFormat(formData.time)}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                backgroundColor: 'rgba(206, 85, 185, 0.1)',
                                color: '#ce55b9',
                                fontFamily: '"Saira", sans-serif'
                              }}
                            />
                          </Box>
                        ) : (
                          'Choose a time from the dropdown'
                        )}
                      </FormHelperText>
                    )}
                  </FormControl>
                ) : (
                  // Modern browsers: Native HTML5 time picker - PICKER-ONLY
                  <TextField
                    fullWidth
                    label="Time"
                    type="time"
                    value={formData.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    onBlur={() => markFieldAsTouched('time')}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: {
                        '& input[type="time"]': {
                          cursor: 'pointer',
                          // Prevent text selection
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                          MozUserSelect: 'none',
                          msUserSelect: 'none'
                        },
                        '& input[type="time"]::-webkit-calendar-picker-indicator': {
                          filter: 'invert(0.5)',
                          cursor: 'pointer',
                          fontSize: '18px',
                          padding: '4px',
                          marginRight: '4px'
                        },
                        '& input[type="time"]::-webkit-calendar-picker-indicator:hover': {
                          filter: 'invert(0.3)',
                          backgroundColor: 'rgba(206, 85, 185, 0.1)',
                          borderRadius: '4px'
                        }
                      },
                      endAdornment: isFieldValid('time') ? (
                        <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                      ) : getFieldError('time') ? (
                        <Error sx={{ color: '#f44336', fontSize: 20 }} />
                      ) : null
                    }}
                    error={!!getFieldError('time')}
                    helperText={
                      getFieldError('time') || (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" sx={{
                            color: '#666',
                            fontFamily: '"Saira", sans-serif'
                          }}>
                            {formData.time ? `Preview: ${convertTo12HourFormat(formData.time)}` : 'Click clock icon to select time'}
                          </Typography>
                          {formData.time && !getFieldError('time') && (
                            <Chip
                              label={convertTo12HourFormat(formData.time)}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'rgba(206, 85, 185, 0.1)',
                                color: '#ce55b9',
                                fontFamily: '"Saira", sans-serif'
                              }}
                            />
                          )}
                        </Box>
                      )
                    }
                    required
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontFamily: '"Saira", sans-serif',
                        '&.Mui-error': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#f44336',
                          }
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(206, 85, 185, 0.5)',
                          }
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: isFieldValid('time') ? '#4caf50' : '#ce55b9',
                          }
                        }
                      },
                      '& .MuiInputLabel-root': {
                        fontFamily: '"Saira", sans-serif',
                        '&.Mui-focused': {
                          color: isFieldValid('time') ? '#4caf50' : '#ce55b9'
                        }
                      },
                      '& .MuiFormHelperText-root': {
                        fontFamily: '"Saira", sans-serif',
                        margin: 0,
                        marginTop: '8px'
                      }
                    }}
                    disabled={submitting}
                    inputProps={{
                      step: 300, // 5 minute intervals
                      onKeyDown: (e) => {
                        // Prevent all keyboard input except navigation keys
                        if (!['Tab', 'Escape', 'Enter'].includes(e.key)) {
                          e.preventDefault();
                        }
                      },
                      onPaste: (e) => {
                        // Prevent pasting
                        e.preventDefault();
                      },
                      onDrop: (e) => {
                        // Prevent drag and drop
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{
              p: 3,
              backgroundColor: 'rgba(206, 85, 185, 0.05)',
              borderRadius: 2,
              border: '1px solid rgba(206, 85, 185, 0.2)'
            }}>
              <Typography variant="subtitle2" sx={{
                mb: 2,
                fontWeight: 600,
                color: '#ce55b9',
                fontFamily: '"Saira", sans-serif',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                🔔 Notification Settings (Email enabled by default)
              </Typography>
              
              {getFieldError('notification_type') && (
                <Alert severity="error" sx={{ mb: 2, fontFamily: '"Saira", sans-serif' }}>
                  <Typography variant="body2" sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {getFieldError('notification_type')}
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.notification_type.includes(1)}
                      onChange={() => handleNotificationChange(1)}
                      disabled={submitting}
                      sx={{
                        '&.Mui-checked': {
                          color: '#ce55b9',
                        },
                      }}
                    />
                  }
                  label={<Typography sx={{ fontFamily: '"Saira", sans-serif' }}>📧 Email notifications</Typography>}
                />
                
              </Box>
              
              <Typography variant="caption" sx={{
                mt: 1,
                color: '#666',
                fontFamily: '"Saira", sans-serif',
                display: 'block'
              }}>
                {formData.notification_type.length === 0 ? 
                  'No notifications selected' : 
                  formData.notification_type.length === 1 && formData.notification_type.includes(1) ?
                    'Email notifications selected (default)' :
                    `${formData.notification_type.length} notification type${formData.notification_type.length > 1 ? 's' : ''} selected`
                }
              </Typography>
            </Box>
          </Box>

          {/* Footer Actions */}
          <Box sx={{
            p: 3,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            background: 'white',
            display: 'flex',
            gap: 2,
            justifyContent: 'flex-end'
          }}>
            <Button
              onClick={closeModal}
              disabled={submitting}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 100
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || (!isFormValid && Object.keys(validationTouched).length > 0)}
              sx={{
                background: isFormValid ? '#ce55b9' : '#ce55b9',
                borderRadius: 2,
                textTransform: 'none',
                fontFamily: '"Saira", sans-serif',
                px: 3,
                py: 1.5,
                minWidth: 140,
                '&:hover': {
                  background: isFormValid ? '#ce55b9' : '#b8479a',
                },
                '&:disabled': {
                  background: '#bdbdbd',
                  color: 'white'
                }
              }}
            >
              {submitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                
                  <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {editMode ? 'Updating...' : 'Creating...'}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {isFormValid && <CheckCircle sx={{ fontSize: 18 }} />}
                  <Typography sx={{ fontFamily: '"Saira", sans-serif' }}>
                    {editMode ? ' Update Todo' : ' Add Todo'}
                  </Typography>
                </Box>
              )}
            </Button>
          </Box>
        </form>
      </Drawer>
    </Grid>
  );
};

export default TodoList;